import "../bootstrap";
import "../styles/index.css";
import "@fontsource/dm-sans/index.css";
import "@fontsource/roboto/index.css";
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import "react-virtualized/styles.css";
import "react-tabs/style/react-tabs.css";

import type { AppProps } from "next/app";
import { I18nProvider } from "@lingui/react";
import { i18n } from "@lingui/core";
import { Fragment, FunctionComponent } from "react";
import { NextComponentType, NextPageContext } from "next";
import Head from "next/head";
import dynamic from "next/dynamic";
import { Web3ReactProvider } from "@web3-react/core";
import getLibrary from "../functions/getLibrary";
import { useEffect } from "react";

import Web3ReactManager from "../components/Web3ReactManager";
import { Provider as ReduxProvider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import store, { persistor } from "../state";
import Dots from "../components/Dots";
import DefaultLayout from "../layouts/Default";
import TransactionUpdater from "../state/transactions/updater";
import ApplicationUpdater from "../state/application/updater";
import { ToastProvider } from '../shared/providers/toast';
if (typeof window !== "undefined" && !!window.ethereum) {
  window.ethereum.autoRefreshOnNetworkChange = false;
}

const Web3ProviderNetwork = dynamic(
  () => import("../components/Web3ProviderNetwork"),
  { ssr: false }
);

export const siteTitle = "MetaDOS staking";

function MyApp({
  Component,
  pageProps,
}: AppProps & {
  Component: NextComponentType<NextPageContext> & {
    Guard: FunctionComponent;
    Layout: FunctionComponent;
    Provider: FunctionComponent;
  };
}) {
  useEffect(() => {
    // Set dark mode by default
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);
  // Allows for conditionally setting a provider to be hoisted per page
  const Provider = Component.Provider || Fragment;

  // Allows for conditionally setting a layout to be hoisted per page
  const Layout = Component.Layout || DefaultLayout;

  // Allows for conditionally setting a guard to be hoisted per page
  const Guard = Component.Guard || Fragment;

  return (
    <Fragment>
      <Head>
        <meta name="viewport" content="width=screen-width, initial-scale=1" />
        <title>{siteTitle}</title>
        <link rel="icon" href="https://stake.doswap.io/favicon.ico" />

        {/* <!-- HTML Meta Tags --> */}
        <title>MetaDOS staking</title>
        <meta
          name="description"
          content="MetaDOS provides staking functions and other services to increase the utilities of $SECOND tokens. We offer both Staking SECOND and Staking NFT functions and the rewards will be in the form of a $SECOND token."
        />

        {/* <!-- Facebook Meta Tags --> */}
        <meta property="og:url" content="https://stake.doswap.io" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="MetaDOS staking" />
        <meta
          property="og:description"
          content="MetaDOS provides staking functions and other services to increase the utilities of $SECOND tokens. We offer both Staking SECOND and Staking NFT functions and the rewards will be in the form of a $SECOND token."
        />
        <meta
          property="og:image"
          content="https://stake.doswap.io/images/thumbnail/meta_thumbnail.png?v1"
        />

        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="staking.MetaDOS.io" />
        <meta property="twitter:url" content="https://stake.doswap.io" />
        <meta name="twitter:title" content="MetaDOS staking" />
        <meta
          name="twitter:description"
          content="MetaDOS provides staking functions and other services to increase the utilities of $SECOND tokens. We offer both Staking SECOND and Staking NFT functions and the rewards will be in the form of a $SECOND token."
        />
        <meta
          name="twitter:image"
          content="https://stake.doswap.io/images/thumbnail/meta_thumbnail.png?v1"
        />
        <meta
          name="thumbnail"
          content="https://stake.doswap.io/images/thumbnail/meta_thumbnail.png?v1"
        />

        {/* <!-- Global site tag (gtag.js) - Google Analytics --> */}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-N0H89JL74J"
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
							window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'G-N0H89JL74J');
						`,
          }}
        />
      </Head>
      <I18nProvider i18n={i18n} forceRenderOnLocaleChange={false}>
        <Web3ReactProvider getLibrary={getLibrary}>
          <Web3ProviderNetwork getLibrary={getLibrary}>
            <Web3ReactManager>
              <ReduxProvider store={store}>
                <PersistGate
                  loading={<Dots>loading</Dots>}
                  persistor={persistor}
                >
                  <TransactionUpdater />
                  <ApplicationUpdater />
                  <Provider>
                    <ToastProvider>
                      <Layout>
                        <Guard>
                          <Component {...pageProps} />
                        </Guard>
                      </Layout>
                    </ToastProvider>
                  </Provider>
                </PersistGate>
              </ReduxProvider>
            </Web3ReactManager>
          </Web3ProviderNetwork>
        </Web3ReactProvider>
      </I18nProvider>
    </Fragment>
  );
}

export default MyApp;
